import { useMemo } from "react";
import { useQuery } from "./useQuery";

const ONE_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

const extractCodeFromQuery = query => {
  return query.get("aff")?.trim() || null;
};

const extractCodeFromPath = urlPath => {
  const affIndex = urlPath.indexOf("/aff/");

  if (affIndex !== -1) {
    const affSegment = urlPath.substring(affIndex + 5); // Skip "/aff/"
    return affSegment.split("/")[0]; // Extract the code before the next "/" or end of URL
  }

  return null;
};

const getLocalStorageKey = checkoutId => {
  return `affiliateCode_${checkoutId}`;
};

const saveCodeToLocalStorage = (localStorageKey, code) => {
  const expiry = Date.now() + ONE_DAY_IN_MILLISECONDS; // 24 hours from now

  localStorage.setItem(localStorageKey, JSON.stringify({ code, expiry }));

  global?.Sentry?.captureMessage(`Affiliate code updated: ${code}`, {
    url: window.location.href,
  });
};

const getValidCodeFromLocalStorage = localStorageKey => {
  const storedData = JSON.parse(
    localStorage.getItem(localStorageKey) || "null",
  );
  return storedData && Date.now() <= storedData.expiry ? storedData.code : null;
};

export const useAffiliateCode = () => {
  const query = useQuery();
  let code = extractCodeFromQuery(query);

  if (!code) {
    code = extractCodeFromPath(window.location.pathname);
  }

  const checkoutId = window.location.pathname.split("/")[1]; // Extract checkout ID from URL
  const localStorageKey = getLocalStorageKey(checkoutId);

  if (code) {
    const existingData = JSON.parse(
      localStorage.getItem(localStorageKey) || "null",
    );

    // Check if the code is different or expired
    if (
      !existingData ||
      existingData.code !== code ||
      Date.now() > existingData.expiry
    ) {
      saveCodeToLocalStorage(localStorageKey, code);
    }
  }

  const validCode = getValidCodeFromLocalStorage(localStorageKey);

  return useMemo(() => validCode, [validCode]);
};
